/**
 * Analytics Caching Service
 * Provides in-memory caching for analytics data with TTL support
 */

/**
 * Default cache configuration
 */
const DEFAULT_CONFIG = {
  maxSize: 1000, // Maximum number of cache entries
  defaultTTL: 5 * 60 * 1000, // 5 minutes default TTL in milliseconds
  cleanupInterval: 2 * 60 * 1000, // 2 minutes cleanup interval
};

/**
 * Cache entry structure
 */
class CacheEntry {
  constructor(data, ttl = DEFAULT_CONFIG.defaultTTL) {
    this.data = data;
    this.timestamp = Date.now();
    this.ttl = ttl;
    this.expiresAt = this.timestamp + ttl;
  }

  /**
   * Check if this cache entry has expired
   */
  isExpired() {
    return Date.now() > this.expiresAt;
  }

  /**
   * Check if this entry is still valid
   */
  isValid() {
    return !this.isExpired();
  }
}

/**
 * Analytics Cache implementation with TTL and size limits
 */
class AnalyticsCache {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.cache = new Map();
    this.accessOrder = new Map(); // Track access order for LRU eviction

    // Start cleanup interval
    this.startCleanup();
  }

  /**
   * Generate cache key from parameters
   * @param {string} websiteId - Website identifier
   * @param {string} dateRange - Date range identifier
   * @param {string} metric - Metric name
   * @returns {string} Cache key
   */
  generateKey(websiteId, dateRange, metric) {
    return `${websiteId}_${dateRange}_${metric}`;
  }

  /**
   * Get cached data if valid
   * @param {string} websiteId - Website identifier
   * @param {string} dateRange - Date range identifier
   * @param {string} metric - Metric name
   * @returns {*|null} Cached data or null if not found/expired
   */
  get(websiteId, dateRange, metric) {
    const key = this.generateKey(websiteId, dateRange, metric);
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    if (entry.isExpired()) {
      this.cache.delete(key);
      this.accessOrder.delete(key);
      return null;
    }

    // Update access order for LRU
    this.accessOrder.set(key, Date.now());

    return entry.data;
  }

  /**
   * Set cached data with optional TTL
   * @param {string} websiteId - Website identifier
   * @param {string} dateRange - Date range identifier
   * @param {string} metric - Metric name
   * @param {*} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds (optional)
   */
  set(websiteId, dateRange, metric, data, ttl) {
    const key = this.generateKey(websiteId, dateRange, metric);

    // Check if we need to evict entries due to size limit
    if (this.cache.size >= this.config.maxSize) {
      this.evictLeastRecentlyUsed();
    }

    const entry = new CacheEntry(data, ttl);
    this.cache.set(key, entry);
    this.accessOrder.set(key, Date.now());
  }

  /**
   * Check if data exists in cache (without retrieving it)
   * @param {string} websiteId - Website identifier
   * @param {string} dateRange - Date range identifier
   * @param {string} metric - Metric name
   * @returns {boolean} True if data exists and is valid
   */
  has(websiteId, dateRange, metric) {
    const key = this.generateKey(websiteId, dateRange, metric);
    const entry = this.cache.get(key);

    if (!entry) {
      return false;
    }

    if (entry.isExpired()) {
      this.cache.delete(key);
      this.accessOrder.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Invalidate all cache entries for a specific website
   * @param {string} websiteId - Website identifier
   */
  invalidate(websiteId) {
    const keysToDelete = [];

    for (const key of this.cache.keys()) {
      if (key.startsWith(`${websiteId}_`)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => {
      this.cache.delete(key);
      this.accessOrder.delete(key);
    });
  }

  /**
   * Invalidate a specific cache entry
   * @param {string} websiteId - Website identifier
   * @param {string} dateRange - Date range identifier
   * @param {string} metric - Metric name
   */
  invalidateKey(websiteId, dateRange, metric) {
    const key = this.generateKey(websiteId, dateRange, metric);
    this.cache.delete(key);
    this.accessOrder.delete(key);
  }

  /**
   * Clear all cached data
   */
  clear() {
    this.cache.clear();
    this.accessOrder.clear();
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    let validEntries = 0;
    let expiredEntries = 0;

    for (const entry of this.cache.values()) {
      if (entry.isValid()) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }

    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
      maxSize: this.config.maxSize,
      hitRate: this.hitCount / (this.hitCount + this.missCount) || 0,
    };
  }

  /**
   * Evict the least recently used entry
   */
  evictLeastRecentlyUsed() {
    if (this.accessOrder.size === 0) {
      return;
    }

    // Find the oldest access time
    let oldestKey = null;
    let oldestTime = Infinity;

    for (const [key, accessTime] of this.accessOrder.entries()) {
      if (accessTime < oldestTime) {
        oldestTime = accessTime;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.accessOrder.delete(oldestKey);
    }
  }

  /**
   * Clean up expired entries
   */
  cleanup() {
    const keysToDelete = [];

    for (const [key, entry] of this.cache.entries()) {
      if (entry.isExpired()) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => {
      this.cache.delete(key);
      this.accessOrder.delete(key);
    });

    return keysToDelete.length;
  }

  /**
   * Start automatic cleanup interval
   */
  startCleanup() {
    if (this.cleanupTimer) {
      return;
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Stop automatic cleanup interval
   */
  stopCleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Destroy the cache and cleanup resources
   */
  destroy() {
    this.stopCleanup();
    this.clear();
  }
}

// Export singleton instance
export const analyticsCache = new AnalyticsCache();

// Export class for custom instances
export { AnalyticsCache };
